import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import {
  LEAD_INTEREST_LEVELS,
  LEAD_SOURCES,
  leadInterestLabel,
  leadSourceLabel,
} from "../lib/leads";

export function NewLeadPage() {
  const navigate = useNavigate();

  const [buildingId, setBuildingId] = useState("");
  const [buildingIds, setBuildingIds] = useState<number[]>([]);
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [preferredMoveInTiming, setPreferredMoveInTiming] = useState("");
  const [unitPreferences, setUnitPreferences] = useState("");
  const [inquirySource, setInquirySource] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [tourScheduledAt, setTourScheduledAt] = useState("");
  const [interestLevel, setInterestLevel] = useState("warm");
  const [contactOfficeIfOverdue, setContactOfficeIfOverdue] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.leads.create({
        buildingId: Number(buildingId),
        buildingIds: buildingIds.length ? buildingIds : [Number(buildingId)],
        contactName: contactName.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        preferredMoveInTiming: preferredMoveInTiming.trim() || null,
        unitPreferences: unitPreferences.trim() || null,
        inquirySource: inquirySource || null,
        followUpDate: followUpDate || null,
        tourScheduledAt: tourScheduledAt || null,
        interestLevel,
        contactOfficeIfOverdue,
        notes: notes.trim() || null,
      }),
    onSuccess: () => navigate("/leads"),
    onError: () => setError("Could not save the lead. Please try again."),
  });

  function onSave() {
    setError(null);
    if (!buildingId) return setError("Please choose a building.");
    if (!contactName.trim()) return setError("Please enter the contact name.");
    createMut.mutate();
  }

  return (
    <Layout title="Add a Prospective Tenant">
      <p className="help-line">
        Capture the inquiry while you have the caller. Only the building and contact name are
        required — fill in the rest if you have it.
      </p>
      <section className="card form-card">
        <label className="field">
          <span>Primary building</span>
          <select
            value={buildingId}
            onChange={(e) => {
              const next = e.target.value;
              setBuildingId(next);
              setBuildingIds(next ? [Number(next)] : []);
            }}
          >
            <option value="">Choose a building…</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="field">
          <span>Buildings interested in</span>
          <div className="choice-set">
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <label
                key={b.id}
                className={"choice" + (buildingIds.includes(b.id) ? " selected" : "")}
              >
                <input
                  type="checkbox"
                  checked={buildingIds.includes(b.id)}
                  onChange={(e) => {
                    setBuildingIds((prev) =>
                      e.target.checked ? [...new Set([...prev, b.id])] : prev.filter((id) => id !== b.id),
                    );
                    if (e.target.checked && !buildingId) setBuildingId(String(b.id));
                  }}
                />
                <span>{b.name}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="field">
          <span>Contact name</span>
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </label>
          <label className="field">
            <span>Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Preferred move-in timing</span>
            <input
              value={preferredMoveInTiming}
              onChange={(e) => setPreferredMoveInTiming(e.target.value)}
              placeholder="e.g. Summer 2026"
            />
          </label>
          <label className="field">
            <span>Inquiry source</span>
            <select value={inquirySource} onChange={(e) => setInquirySource(e.target.value)}>
              <option value="">Not set</option>
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {leadSourceLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span>Unit preferences</span>
          <input
            value={unitPreferences}
            onChange={(e) => setUnitPreferences(e.target.value)}
            placeholder="e.g. One-bedroom, ground floor"
          />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Follow-up date</span>
            <input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Tour scheduled</span>
            <input
              type="datetime-local"
              value={tourScheduledAt}
              onChange={(e) => setTourScheduledAt(e.target.value)}
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Activity / interest</span>
            <select value={interestLevel} onChange={(e) => setInterestLevel(e.target.value)}>
              {LEAD_INTEREST_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {leadInterestLabel(level)}
                </option>
              ))}
            </select>
          </label>
          <label className="checkbox-line">
            <input
              type="checkbox"
              checked={contactOfficeIfOverdue}
              onChange={(e) => setContactOfficeIfOverdue(e.target.checked)}
            />
            <span>Contact office if overdue</span>
          </label>
        </div>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {error && <p className="field-error">{error}</p>}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/leads")}
            disabled={createMut.isPending}
          >
            Cancel
          </button>
          <button className="btn btn-primary btn-xl" onClick={onSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Save lead"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
